import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, ChevronRight } from 'lucide-react';

const sections = [
  { id: 'acceptance', num: '01', title: 'Acceptance of Terms', content: (
    <p>
      By accessing and using the Expertize Edge website and services, you agree to be bound 
      by these Terms of Service and all applicable laws and regulations. If you do not agree 
      with any of these terms, you are prohibited from using or accessing this site.
    </p>
  )},
  { id: 'services', num: '02', title: 'Services Description', content: (
    <p>
      Expertize Edge provides professional services including corporate training, consulting 
      and strategy advisory, coaching and mentorship programs, DPDP compliance services, and 
      the Luminarc™ experience. Specific terms for individual services will be provided in 
      separate service agreements.
    </p>
  )},
  { id: 'accounts', num: '03', title: 'User Accounts', content: (
    <>
      <p className="mb-4">When you create an account with us, you must:</p>
      <ul className="space-y-3">
        {['Provide accurate, complete, and current information', 'Maintain the security of your password and account', 'Accept responsibility for all activities under your account', 'Notify us immediately of any unauthorized use'].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <ChevronRight className="w-5 h-5 text-brand-purple flex-shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </>
  )},
  { id: 'ip', num: '04', title: 'Intellectual Property', content: (
    <p>
      The content, features, and functionality of our website and services, including but not 
      limited to text, graphics, logos, images, and software, are owned by Expertize Edge and 
      are protected by international copyright, trademark, patent, trade secret, and other 
      intellectual property laws. You may not reproduce, distribute, modify, or create 
      derivative works without our express written consent.
    </p>
  )},
  { id: 'confidentiality', num: '05', title: 'Confidentiality', content: (
    <p>
      Both parties agree to maintain the confidentiality of any proprietary or confidential 
      information disclosed during the course of our engagement. This includes but is not 
      limited to business strategies, methodologies, client information, and training materials.
    </p>
  )},
  { id: 'payment', num: '06', title: 'Payment Terms', content: (
    <p>
      Payment terms for services will be specified in individual service agreements. 
      All fees are non-refundable unless otherwise stated. We reserve the right to modify 
      our pricing with reasonable notice.
    </p>
  )},
  { id: 'liability', num: '07', title: 'Limitation of Liability', content: (
    <p>
      To the maximum extent permitted by law, Expertize Edge shall not be liable for any 
      indirect, incidental, special, consequential, or punitive damages, or any loss of 
      profits or revenues, whether incurred directly or indirectly, or any loss of data, 
      use, goodwill, or other intangible losses resulting from your use of our services.
    </p>
  )},
  { id: 'warranties', num: '08', title: 'Disclaimer of Warranties', content: (
    <p>
      Our services are provided "as is" and "as available" without warranties of any kind, 
      either express or implied. We do not guarantee that our services will meet your specific 
      requirements or that results will be achieved. Success depends on numerous factors 
      including client engagement and implementation.
    </p>
  )},
  { id: 'indemnification', num: '09', title: 'Indemnification', content: (
    <p>
      You agree to indemnify and hold harmless Expertize Edge and its officers, directors, 
      employees, and agents from any claims, damages, losses, liabilities, and expenses 
      arising out of your use of our services or violation of these Terms.
    </p>
  )},
  { id: 'termination', num: '10', title: 'Termination', content: (
    <p>
      We may terminate or suspend your access to our services immediately, without prior 
      notice or liability, for any reason, including breach of these Terms. Upon termination, 
      your right to use our services will cease immediately.
    </p>
  )},
  { id: 'governing-law', num: '11', title: 'Governing Law', content: (
    <p>
      These Terms shall be governed by and construed in accordance with the laws of India, 
      without regard to its conflict of law provisions. Any disputes arising under these Terms 
      shall be subject to the exclusive jurisdiction of the courts of Gujarat, India.
    </p>
  )},
  { id: 'changes', num: '12', title: 'Changes to Terms', content: (
    <p>
      We reserve the right to modify these Terms at any time. We will provide notice of 
      significant changes by posting the updated Terms on our website. Your continued use 
      of our services after such changes constitutes acceptance of the new Terms.
    </p>
  )},
  { id: 'contact', num: '13', title: 'Contact Information', content: (
    <>
      <p className="mb-4">For questions about these Terms of Service, please contact us at:</p>
      <div className="bg-brand-charcoal rounded-xl p-6 text-white">
        <p className="font-semibold text-brand-gold mb-2">Expertize Edge</p>
        <p className="text-gray-300">Email: contact@expertizeedge.com</p>
        <p className="text-gray-300">Phone: +91 95757 37655</p>
        <p className="text-gray-300">Gandhinagar, Gujarat, India</p>
      </div>
    </>
  )},
];

const TermsOfServicePage = () => {
  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-brand-charcoal overflow-hidden" data-testid="terms-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(92,45,145,0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(196,158,77,0.15),transparent_50%)]" />
        </div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/3 left-1/4 w-40 h-40 bg-brand-gold/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
              <FileText className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Legal</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-4">
              Terms of <span className="text-brand-gold">Service</span>
            </h1>
            <p className="text-gray-400 text-lg">Last updated: January 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-brand-bg-light" data-testid="terms-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
            {/* Sidebar TOC */}
            <aside className="hidden lg:block">
              <nav className="sticky top-28 space-y-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block text-sm px-3 py-2 rounded-lg transition-all duration-200 ${
                      activeSection === s.id
                        ? 'bg-brand-purple/10 text-brand-purple font-semibold'
                        : 'text-brand-muted hover:text-brand-charcoal hover:bg-white'
                    }`}
                  >
                    <span className="text-xs text-brand-gold mr-2">{s.num}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </aside>

            {/* Main Content */}
            <div className="space-y-8">
              {sections.map((s, index) => (
                <motion.div
                  key={s.id}
                  id={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white rounded-2xl p-8 md:p-10 border border-brand-lavender hover:border-brand-purple/20 hover:shadow-lg transition-all duration-300"
                  data-testid={`terms-section-${s.id}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs font-bold text-brand-gold bg-brand-gold/10 px-2.5 py-1 rounded-md">{s.num}</span>
                    <h2 className="font-heading text-2xl font-bold text-brand-charcoal">{s.title}</h2>
                  </div>
                  <div className="text-brand-body leading-relaxed">
                    {s.content}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsOfServicePage;
